"use client";

import {JSX, useCallback, useRef} from "react";

import {useKeenSlider} from "keen-slider/react";

import {useSliderNavigation} from "@/sections/content/video-testimonials/hooks/use-slider-navigation";
import {useSliderState} from "@/sections/content/video-testimonials/hooks/use-slider-state";
import {useVideoInteraction} from "@/sections/content/video-testimonials/hooks/use-video-interaction";
import {VideoTestimonialsNavigation} from "@/sections/content/video-testimonials/parts/video-testimonials-navigation";
import {VideoTestimonialsReview} from "@/sections/content/video-testimonials/parts/video-testimonials-review";
import {VideoTestimonialsSlide} from "@/sections/content/video-testimonials/parts/video-testimonials-slide";
import {VideoTestimonialsTitle} from "@/sections/content/video-testimonials/parts/video-testimonials-title";
import {useSectionSpace} from "@/sections/use-section-space";

import {useVideoIntersection} from "@/shared/hooks";

import type {VideoTestimonialsProps} from "./video-testimonials.types";

import "@/sections/content/video-testimonials/styles/video-testemonials.scss";
import "keen-slider/keen-slider.min.css";

export function VideoTestimonialsRoot({space, videos, children, anchorId}: VideoTestimonialsProps): JSX.Element | null {
    const sectionVars = useSectionSpace(space);
    const sectionRef = useRef<HTMLElement | null>(null);

    const {currentIndex, sliderInstance, sliderConfig, handleSliderInit} = useSliderState(videos);
    const {handlePrev, handleNext, handleGoTo} = useSliderNavigation(sliderInstance, videos.length);
    const {playingIndex, handlePlay, stopAll} = useVideoInteraction();

    const handlePrevWithStop = useCallback((): void => {
        stopAll();
        handlePrev();
    }, [stopAll, handlePrev]);

    const handleNextWithStop = useCallback((): void => {
        stopAll();
        handleNext();
    }, [stopAll, handleNext]);

    const handleGoToWithStop = useCallback(
        (index: number): void => {
            stopAll();
            handleGoTo(index);
        },
        [stopAll, handleGoTo],
    );

    useVideoIntersection(sectionRef, {
        containerRef: () => sliderInstance?.container || null,
    });

    const [sliderRef] = useKeenSlider<HTMLDivElement>(sliderConfig, [handleSliderInit]);

    const handleSlideClick = useCallback(
        (index: number): void => {
            if (index === currentIndex || videos.length <= 1) {
                return;
            }

            const total = videos.length;
            const prevIndex = (currentIndex - 1 + total) % total;
            const nextIndex = (currentIndex + 1) % total;

            stopAll();

            if (index === prevIndex) {
                handlePrev();
            } else if (index === nextIndex) {
                handleNext();
            }
        },
        [currentIndex, videos.length, handlePrev, handleNext, stopAll],
    );

    if (!videos || videos.length === 0) {
        return null;
    }

    const hasNavigation = videos.length > 1;
    const currentVideo = videos[currentIndex];
    const currentReview = currentVideo?.review;

    return (
        <section ref={sectionRef} className="video-testimonials" style={sectionVars} id={anchorId}>
            {children && <div className="video-testimonials__header">{children}</div>}
            <div ref={sliderRef} className="keen-slider video-testimonials__slider">
                {videos.map((video, index) => (
                    <VideoTestimonialsSlide
                        key={video.id}
                        video={video}
                        index={index}
                        isActive={index === currentIndex}
                        isPlaying={playingIndex === index}
                        onPlay={handlePlay}
                        onSlideClick={handleSlideClick}
                    />
                ))}
            </div>
            <div className="video-testimonials__content">
                {hasNavigation && (
                    <VideoTestimonialsNavigation
                        currentIndex={currentIndex}
                        totalVideos={videos.length}
                        hasNavigation={hasNavigation}
                        onPrev={handlePrevWithStop}
                        onNext={handleNextWithStop}
                        onGoTo={handleGoToWithStop}
                    />
                )}
                {currentReview && <VideoTestimonialsReview review={currentReview} />}
            </div>
        </section>
    );
}

type VideoTestimonialsComponent = typeof VideoTestimonialsRoot & {
    Title: typeof VideoTestimonialsTitle;
};

export const VideoTestimonials = Object.assign(VideoTestimonialsRoot, {
    Title: VideoTestimonialsTitle,
}) as VideoTestimonialsComponent;
